import * as _ from 'lodash'
import {PASearch} from "../PA.Core/PASearch";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {IUsrContext} from "../PA.Core/PA.Core.IF.User";

export class UserSecurityService {

    // getUserGroupPlanList/getUserGroupCustomerList are re-queried on every search request;
    // cache their result per usrContext instance. UserGroups/WB_SalesOrgs are only populated
    // once in PAusrContext.Load(), so the one invalidation trigger that actually matters is a
    // "LoginStatus" change (re-login as a different user on the same browser tab); dropping the
    // cache on every OnContextChange (including a same-user sales org/area switch) is just
    // harmless extra safety, not a correctness requirement.
    private static _usrGroupPlanIdsCache: WeakMap<IUsrContext, Promise<number[]>> = new WeakMap();
    private static _usrGroupCustomerIdsCache: WeakMap<IUsrContext, Promise<number[]>> = new WeakMap();
    private static _usrGroupProductGroupIdsCache: WeakMap<IUsrContext, Promise<number[]>> = new WeakMap();
    private static _observedUsrContexts: WeakSet<IUsrContext> = new WeakSet();

    private static observeContextChanges(usrContext: IUsrContext): void {
        if (UserSecurityService._observedUsrContexts.has(usrContext)) {
            return
        }
        UserSecurityService._observedUsrContexts.add(usrContext)
        usrContext.OnContextChange.subscribe((evt) => {
            if (_.isEmpty(evt)) {
                return
            }
            UserSecurityService._usrGroupPlanIdsCache.delete(usrContext)
            UserSecurityService._usrGroupCustomerIdsCache.delete(usrContext)
            UserSecurityService._usrGroupProductGroupIdsCache.delete(usrContext)
        })
    }

    static async getResponsibleUserList(customerWbID: number,
                                        periodWbID: number,
                                        requiredRole_any_one?: string[],
                                        requiredRole2_any_one?: string[],
                                        approvalLimitThreshold?: number): Promise<number[]> {

        if (_.isEmpty(requiredRole_any_one)) {
            requiredRole_any_one = [];
        }
        if (_.isEmpty(requiredRole2_any_one)) {
            requiredRole2_any_one = [];
        }

        let authUserWbIDs: number[];

        let whereJSON: any = {};
        whereJSON.and = [];
        let whereFieldSpecJSON: any = {};
        whereJSON.WB_CUST_ID = customerWbID;
        whereFieldSpecJSON.WB_CUST_ID = {type: "number"};
        whereFieldSpecJSON.ROLE_ID = {type: "string"};

        if (!_.isEmpty(requiredRole_any_one)) {
            whereJSON.and.push({ROLE_ID: {inq: requiredRole_any_one}});
        }

        if (!_.isEmpty(requiredRole2_any_one)) {
            whereJSON.and.push({ROLE_ID: {inq: requiredRole2_any_one}});
        }

        let results = await PASearch.find("vUI_UserRoles_All",
            whereJSON,
            whereFieldSpecJSON,
            [], 0,
            []);

        if (_.isArray(results) && !_.isEmpty(results)) {
            authUserWbIDs = _.uniq(_.map(results, (aRow) => {
                return aRow.WB_AUTHUSR_ID
            }))
        } else {
            authUserWbIDs = [];
        }

        return authUserWbIDs;
    }

    // Replaces getResponsibleUserListWithPlanManagement: finds users (not just the current user)
    // responsible for a specific customer/sales-org/product-group combination, scoped by each
    // candidate user's own user-group customer/sales-org/product-group assignments rather than
    // Plan Management membership.
    static async getResponsibleUserListByScope(customerWbID: number,
                                                salesOrgWbID: number,
                                                productGroupWbID: number,
                                                requiredRole_any_one?: string[]): Promise<number[]> {
        if (_.isEmpty(requiredRole_any_one)) {
            requiredRole_any_one = [];
        }

        let authUserWbIDs: number[];

        let whereJSON: any = {};
        whereJSON.and = [];
        let whereFieldSpecJSON: any = {
            WB_CUST_ID: {type: "number"},
            WB_SALESORG_ID: {type: "number"},
            WB_PRDGRP_ID: {type: "number"},
            ROLE_ID: {type: "string"}
        };
        whereJSON.WB_CUST_ID = customerWbID;
        whereJSON.WB_SALESORG_ID = salesOrgWbID;
        whereJSON.WB_PRDGRP_ID = productGroupWbID;

        if (!_.isEmpty(requiredRole_any_one)) {
            whereJSON.and.push({ROLE_ID: {inq: requiredRole_any_one}});
        }

        let results = await PASearch.find("vUI_UsrGrp_UserRoles_All",
            whereJSON,
            whereFieldSpecJSON,
            [], 0,
            []);

        if (_.isArray(results) && !_.isEmpty(results)) {
            authUserWbIDs = _.uniq(_.map(results, (aRow) => {
                return aRow.WB_AUTHUSR_ID
            }))
        } else {
            authUserWbIDs = [];
        }

        return authUserWbIDs;
    }

    static getUserGroupPlanList(): Promise<number[]> {
        let usrContext: IUsrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
        if (!(usrContext && usrContext.User)) {
            return Promise.resolve([])
        }

        UserSecurityService.observeContextChanges(usrContext)

        let cachedPlanIds = UserSecurityService._usrGroupPlanIdsCache.get(usrContext)
        if (cachedPlanIds) {
            return cachedPlanIds
        }

        let planIdsPromise = (async (): Promise<number[]> => {
            let whereJSON: any = {};
            whereJSON.and = [];
            whereJSON.and.push({WB_SALESORG_ID: {inq: _.map(usrContext.WB_SalesOrgs, 'WB_SALESORG_ID') } })
            whereJSON.and.push({WB_USRGRP_ID: {inq: _.map(usrContext.UserGroups, 'WB_USRGRP_ID') } })
            let whereFieldSpecJSON: any = {
                WB_SALESORG_ID: {type: "number"},
                WB_USRGRP_ID: {type: "number"}};

            let results = await PASearch.find("vUI_UsrGrp_Plans",
                whereJSON,
                whereFieldSpecJSON,
                [], 0,
                ['WB_PLAN_ID']);

            if (_.isArray(results) && !_.isEmpty(results)) {
                return _.uniq(_.map(results, (aRow) => {
                    return aRow.WB_PLAN_ID
                }))
            } else {
                return [];
            }
        })().catch((err) => {
            // Don't let one transient failure permanently poison the cache for the rest of the session
            UserSecurityService._usrGroupPlanIdsCache.delete(usrContext)
            throw err
        })

        UserSecurityService._usrGroupPlanIdsCache.set(usrContext, planIdsPromise)
        return planIdsPromise
    }

    static getUserGroupCustomerList(): Promise<number[]> {
        let usrContext: IUsrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
        if (!(usrContext && usrContext.User)) {
            return Promise.resolve([])
        }

        UserSecurityService.observeContextChanges(usrContext)

        let cachedCustomerIds = UserSecurityService._usrGroupCustomerIdsCache.get(usrContext)
        if (cachedCustomerIds) {
            return cachedCustomerIds
        }

        let customerIdsPromise = (async (): Promise<number[]> => {
            let whereJSON: any = {};
            whereJSON.and = [];
            whereJSON.and.push({WB_USRGRP_ID: {inq: _.map(usrContext.UserGroups, 'WB_USRGRP_ID') } })
            let whereFieldSpecJSON: any = {
                WB_USRGRP_ID: {type: "number"}};

            let results = await PASearch.find("vUI_UsrGrp_Customers",
                whereJSON,
                whereFieldSpecJSON,
                [], 0,
                ['WB_CUST_ID']);

            if (_.isArray(results) && !_.isEmpty(results)) {
                return _.uniq(_.map(results, (aRow) => {
                    return aRow.WB_CUST_ID
                }))
            } else {
                return [];
            }
        })().catch((err) => {
            // Don't let one transient failure permanently poison the cache for the rest of the session
            UserSecurityService._usrGroupCustomerIdsCache.delete(usrContext)
            throw err
        })

        UserSecurityService._usrGroupCustomerIdsCache.set(usrContext, customerIdsPromise)
        return customerIdsPromise
    }

    static getUserGroupProductGroupList(): Promise<number[]> {
        let usrContext: IUsrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
        if (!(usrContext && usrContext.User)) {
            return Promise.resolve([])
        }

        UserSecurityService.observeContextChanges(usrContext)

        let cachedProductGroupIds = UserSecurityService._usrGroupProductGroupIdsCache.get(usrContext)
        if (cachedProductGroupIds) {
            return cachedProductGroupIds
        }

        let productGroupIdsPromise = (async (): Promise<number[]> => {
            let whereJSON: any = {};
            whereJSON.and = [];
            whereJSON.and.push({WB_USRGRP_ID: {inq: _.map(usrContext.UserGroups, 'WB_USRGRP_ID') } })
            let whereFieldSpecJSON: any = {
                WB_USRGRP_ID: {type: "number"}};

            let results = await PASearch.find("vUI_UsrGrp_ProductGroups",
                whereJSON,
                whereFieldSpecJSON,
                [], 0,
                ['WB_PRDGRP_ID']);

            if (_.isArray(results) && !_.isEmpty(results)) {
                return _.uniq(_.map(results, (aRow) => {
                    return aRow.WB_PRDGRP_ID
                }))
            } else {
                return [];
            }
        })().catch((err) => {
            // Don't let one transient failure permanently poison the cache for the rest of the session
            UserSecurityService._usrGroupProductGroupIdsCache.delete(usrContext)
            throw err
        })

        UserSecurityService._usrGroupProductGroupIdsCache.set(usrContext, productGroupIdsPromise)
        return productGroupIdsPromise
    }

    // Replaces Plan Management membership as the access-control gate: a plan is in scope when its
    // sales org, planning customer, and planning-level product group are each covered by the
    // current user's own sales orgs and user-group customer/product-group assignments.
    static async isPlanWithinUserGroupScope(aWBSalesOrgID: number, aWBCustIdPlanning: number, aWBPrdGrpIdPlanLevel: number): Promise<boolean> {
        let usrContext: IUsrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
        if (!(usrContext && usrContext.User)) {
            return false
        }

        let [arrWBSalesOrgIds, arrWBCustIds, arrWBPrdGrpIds] = await Promise.all([
            Promise.resolve(_.map(usrContext.WB_SalesOrgs, 'WB_SALESORG_ID')),
            UserSecurityService.getUserGroupCustomerList(),
            UserSecurityService.getUserGroupProductGroupList()
        ])

        return _.includes(arrWBSalesOrgIds, aWBSalesOrgID)
            && _.includes(arrWBCustIds, aWBCustIdPlanning)
            && _.includes(arrWBPrdGrpIds, aWBPrdGrpIdPlanLevel)
    }

}
